<template>
    <div class="max-w-6xl mx-auto pb-8 space-y-6">
        <Loading :overlay="true" :show="savingConclusion" overlay-text="salvando edição..." />
        <ConclusionGenerationOverlay :show="generatingConclusion" />
        <PdfExportOverlay :show="exportingPdf" />

        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
                <h1 class="text-2xl font-bold text-gray-800">Conclusão com IA</h1>
                <p class="mt-1 text-sm text-gray-500">Página dedicada para revisar, editar e aprofundar a conclusão da avaliação.</p>
            </div>

            <div class="flex flex-wrap gap-2">
                <BaseButton skin="primary-outline-small" @click="goToRecords">Voltar ao prontuário</BaseButton>
                <BaseButton v-if="record?.uuid" skin="primary-outline-small" @click="goToAvaliation">Abrir avaliação</BaseButton>
                <BaseButton v-if="conclusion?.uuid" skin="primary-small" :disabled="exportingPdf" @click="exportPdf">
                    {{ exportingPdf ? 'Exportando...' : 'Exportar PDF' }}
                </BaseButton>
            </div>
        </div>

        <template v-if="loading">
            <section class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="skeleton h-7 w-56 rounded-xl"></div>
                <div class="mt-2 skeleton h-4 w-80 rounded"></div>
                <div class="mt-6 grid gap-4 lg:grid-cols-2">
                    <div v-for="i in 2" :key="`meta-${i}`" class="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                        <div class="skeleton h-3 w-24 rounded"></div>
                        <div class="mt-3 skeleton h-4 w-32 rounded"></div>
                    </div>
                </div>
            </section>

            <section class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_340px]">
                    <div class="skeleton min-h-[420px] rounded-[28px]"></div>
                    <div class="space-y-4">
                        <div class="skeleton h-48 rounded-[24px]"></div>
                        <div class="skeleton h-56 rounded-[24px]"></div>
                    </div>
                </div>
            </section>
        </template>

        <template v-else>
            <section class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div>
                        <div class="flex flex-wrap items-center gap-2">
                            <h2 class="text-xl font-bold text-slate-800">{{ patient?.name || 'Paciente' }}</h2>
                            <span v-if="conclusion" class="rounded-full bg-cyan-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-700">
                                {{ conclusion.type === 'acia' ? 'ACIA' : 'Conclusão padrão' }}
                            </span>
                        </div>
                        <p class="mt-1 text-sm text-slate-500">
                            Avaliação de {{ formatDate(record?.created_at) }} · {{ formatRelative(record?.created_at) }}
                        </p>
                    </div>

                    <div class="grid gap-3 sm:grid-cols-1">
                        <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4">
                            <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Gerada em</div>
                            <div class="mt-1 text-sm font-semibold text-slate-700">{{ formatDateTime(conclusion?.generated_at) }}</div>
                        </div>
                    </div>
                </div>
            </section>

            <section v-if="!conclusion" class="rounded-[28px] border border-amber-200 bg-amber-50 p-6 shadow-sm">
                <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div class="text-sm text-amber-900">
                        Ainda não existe conclusão gerada para esta avaliação. Gere agora para abrir o relatório completo com IA.
                    </div>
                    <BaseButton skin="primary-small" :disabled="generatingConclusion" @click="generateConclusion()">
                        {{ generatingConclusion ? 'Gerando...' : 'Gerar conclusão com IA' }}
                    </BaseButton>
                </div>
            </section>

            <section v-else class="rounded-[28px] border border-gray-100 bg-white p-6 shadow-sm">
                <div class="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_340px]">
                    <div class="space-y-4">
                        <div>
                            <div class="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Texto editável</div>
                            <div
                                ref="editableRef"
                                contenteditable="true"
                                class="conclusion-editable prose prose-sm min-h-[460px] max-w-none rounded-[28px] border border-slate-200 bg-white px-5 py-5 text-sm leading-7 text-slate-700 shadow-sm focus:border-cyan-300 focus:outline-none prose-headings:font-semibold prose-headings:text-cyan-700 prose-p:my-4 prose-p:text-slate-600 prose-li:text-slate-600"
                                @input="onEditableInput"
                            ></div>
                        </div>
                    </div>

                    <div class="space-y-4">
                        <div class="rounded-[24px] border border-slate-100 bg-slate-50/80 p-5">
                            <div class="text-sm font-semibold text-slate-800">Ações</div>
                            <div class="mt-4 flex flex-col gap-2">
                                <button type="button" class="btn btn-primary" :disabled="savingConclusion || generatingConclusion" @click="saveConclusion">
                                    {{ savingConclusion ? 'Salvando...' : 'Salvar edição' }}
                                </button>
                                <button type="button" class="btn conclusion-ai-cta flex gap-x-2" :disabled="savingConclusion || generatingConclusion" @click="confirmRegenerate">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                                    </svg>
                                    {{ generatingConclusion ? 'Gerando...' : 'Gerar novamente com IA' }}
                                </button>
                                <button type="button" class="btn btn-outline" :disabled="exportingPdf || generatingConclusion" @click="exportPdf">
                                    {{ exportingPdf ? 'Exportando...' : 'Exportar PDF' }}
                                </button>
                            </div>
                        </div>

                        <div class="rounded-[24px] border border-slate-100 bg-slate-50/80 p-5">
                            <div class="text-sm font-semibold text-slate-800">Resumo visual</div>
                            <div class="mt-4 space-y-4">
                                <div>
                                    <div class="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Perfil de risco</div>
                                    <div class="space-y-2">
                                        <div v-for="item in riskProfileChart" :key="item.label" class="grid grid-cols-[90px_1fr_28px] items-center gap-3">
                                            <div class="text-xs font-semibold text-slate-500">{{ item.label }}</div>
                                            <div class="h-2.5 overflow-hidden rounded-full bg-slate-200">
                                                <div class="h-full rounded-full" :style="{ width: `${item.width}%`, backgroundColor: item.color }"></div>
                                            </div>
                                            <div class="text-right text-xs font-semibold text-slate-500">{{ item.value }}</div>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div class="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Probabilidade</div>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div v-for="item in probabilityChart" :key="item.label" class="rounded-2xl bg-white px-3 py-3 shadow-sm ring-1 ring-slate-100">
                                            <div class="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">{{ item.label }}</div>
                                            <div class="mt-1 text-lg font-bold text-slate-700">{{ item.value }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="rounded-[24px] border border-slate-100 bg-slate-50/80 p-5">
                            <div class="text-sm font-semibold text-slate-800">Principais correlações</div>
                            <div v-if="conclusion.key_correlations && conclusion.key_correlations.length" class="mt-4 space-y-2">
                                <div
                                    v-for="(correlation, index) in conclusion.key_correlations"
                                    :key="`correlation-${index}`"
                                    class="rounded-[22px] bg-white px-4 py-4 text-sm text-slate-600 shadow-sm ring-1 ring-slate-100"
                                >
                                    <template v-if="isStructuredCorrelation(correlation)">
                                        <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Correlação {{ index + 1 }}</div>

                                        <div class="mt-3 space-y-3">
                                            <div>
                                                <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-600">Achado principal</div>
                                                <div class="mt-1 leading-6 text-slate-700">{{ correlation.finding_a }}</div>
                                            </div>

                                            <div>
                                                <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-600">Contexto relacionado</div>
                                                <div class="mt-1 leading-6 text-slate-700">{{ correlation.finding_b }}</div>
                                            </div>

                                            <div>
                                                <div class="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-600">Leitura clínica</div>
                                                <div class="mt-1 leading-6 text-slate-700">{{ correlation.clinical_significance }}</div>
                                            </div>
                                        </div>
                                    </template>

                                    <template v-else>
                                        {{ formatCorrelation(correlation) }}
                                    </template>
                                </div>
                            </div>
                            <div v-else class="mt-4 text-sm text-slate-400">
                                Nenhuma correlação estruturada registrada nesta conclusão.
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </template>

        <!-- Fora da tela: só existe para virar imagem da matriz no PDF exportado.
             O deslocamento fica no palco externo — o nó capturado precisa continuar
             estático, senão o html-to-image devolve uma imagem em branco. -->
        <div v-if="matrix && matrix.length" class="export-matrix-stage">
            <div id="conclusion-matrix-container" class="inline-flex items-start gap-6 bg-white p-2">
                <RiskMatrix :data="matrix"></RiskMatrix>
                <BodyRiskMap v-if="showBodyRiskMap" :data="matrix" :width="170"></BodyRiskMap>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Swal from 'sweetalert';
import Loading from '@/components/Loading';
import ConclusionGenerationOverlay from '@/components/ConclusionGenerationOverlay.vue';
import PdfExportOverlay from '@/components/PdfExportOverlay.vue';
import RiskMatrix from '@/components/RiskMatrix.vue';
import BodyRiskMap from '@/components/BodyRiskMap.vue';
import { PatientService, RecordService, MatrixService, ConclusionService, UsageService } from '@/services/Entity';
import { unwrapAiHtml } from '@shared/ai';
import AuthService from '@/services/AuthService';
import { PLANS } from '@/constants/plans';
import { SHOW_BODY_RISK_MAP } from '@/constants/features';
import { classifyRiskBand, RISK_BAND_COLORS } from '@/utils/risk';
import { captureMatrixPng } from '@/utils/matrixCapture';

const MATRIX_ELEMENT_ID = 'conclusion-matrix-container';

export default {
    components: {
        Loading,
        ConclusionGenerationOverlay,
        PdfExportOverlay,
        RiskMatrix,
        BodyRiskMap
    },
    setup() {
        const route = useRoute();
        const router = useRouter();
        const loading = ref(true);
        const savingConclusion = ref(false);
        const generatingConclusion = ref(false);
        const exportingPdf = ref(false);
        const patient = ref(null);
        const record = ref(null);
        const conclusion = ref(null);
        const conclusionDraft = ref('');
        const editableRef = ref(null);
        const matrix = ref([]);
        const usageInfo = ref(null);
        const highestPlanId = PLANS[PLANS.length - 1]?.id || 'standard';
        const showBodyRiskMap = SHOW_BODY_RISK_MAP;

        const riskProfileChart = computed(() => {
            const counts = Object.fromEntries(
                Object.entries(RISK_BAND_COLORS).map(([band, color]) => [band, { value: 0, color }])
            );

            (matrix.value || []).forEach((item) => {
                const band = classifyRiskBand(item);
                counts[band].value += 1;
            });

            const max = Math.max(1, ...Object.values(counts).map((item) => item.value));
            return Object.entries(counts).map(([label, item]) => ({
                label,
                value: item.value,
                width: (item.value / max) * 100,
                color: item.color
            }));
        });

        const probabilityChart = computed(() => {
            const counts = { Baixa: 0, Média: 0, Alta: 0, 'Muito alta': 0 };

            (matrix.value || []).forEach((item) => {
                if (item.probability === 'low') counts.Baixa += 1;
                if (item.probability === 'medium') counts.Média += 1;
                if (item.probability === 'high') counts.Alta += 1;
                if (item.probability === 'very_high') counts['Muito alta'] += 1;
            });

            return Object.entries(counts).map(([label, value]) => ({ label, value }));
        });

        onMounted(async () => {
            await loadPage();
        });

        async function loadPage() {
            loading.value = true;

            const patientUuid = route.params.patient_uuid;
            const recordUuid = route.params.record_uuid;

            if (!patientUuid || !recordUuid) {
                router.push({ name: 'patients' });
                return;
            }

            try {
                await Promise.all([
                    loadUsage(),
                    loadPatient(patientUuid),
                    loadRecord(recordUuid),
                    loadConclusion(recordUuid)
                ]);

                // Só a matriz da própria avaliação. Sem ela, o PDF sai sem o mapa
                // corporal — usar a mais recente do paciente mostrava a matriz de outra
                // avaliação ao lado de um texto gerado sem matriz nenhuma.
                if (record.value?.matrix_uuid) {
                    await loadMatrix(record.value.matrix_uuid);
                }
            } catch (err) {
                Swal({
                    title: 'Ocorreu um problema ao abrir a conclusão',
                    text: 'Atualize a página e tente novamente. Se o erro persistir, entre em contato com o suporte.',
                    icon: 'error'
                });
            } finally {
                loading.value = false;
            }
        }

        async function loadUsage() {
            try {
                const { data } = await UsageService.getUsage();
                usageInfo.value = (data && data.data) || null;
                return usageInfo.value;
            } catch (err) {
                return usageInfo.value;
            }
        }

        async function loadPatient(patientUuid) {
            const { data } = await PatientService.getByUUID(patientUuid);
            patient.value = data;
        }

        async function loadRecord(recordUuid) {
            const { data } = await RecordService.getByUUID(recordUuid);
            record.value = data;
        }

        async function loadConclusion(recordUuid) {
            const { data } = await ConclusionService.getByRecord(recordUuid);
            conclusion.value = pickLatestConclusion((data && data.data) || []);
            conclusionDraft.value = normalizeConclusionHtml(conclusion.value?.text_content || '');
        }

        async function loadMatrix(matrixUuid) {
            try {
                const { data } = await MatrixService.getByUUID(matrixUuid);
                matrix.value = (data && data.info) || [];
            } catch (err) {
                matrix.value = [];
            }
        }

        function sortConclusions(conclusions = []) {
            return [...conclusions].sort((a, b) => new Date(b.generated_at || b.updated_at || b.created_at || 0) - new Date(a.generated_at || a.updated_at || a.created_at || 0));
        }

        function pickLatestConclusion(conclusions = []) {
            return sortConclusions(conclusions)[0] || null;
        }

        async function showUsageLimitModal() {
            const usage = usageInfo.value || await loadUsage();

            if (usage && usage.plan && usage.plan !== highestPlanId) {
                const shouldUpgrade = await Swal({
                    title: 'Limite de IA atingido',
                    text: 'Seu plano atual não possui mais créditos para gerar conclusões com IA. Faça upgrade para continuar.',
                    icon: 'warning',
                    buttons: {
                        cancel: 'Agora não',
                        confirm: {
                            text: 'Ver planos',
                            value: true
                        }
                    }
                });

                if (shouldUpgrade) {
                    router.push({ name: 'subscription' });
                }
                return;
            }

            await Swal({
                title: 'Sem créditos disponíveis',
                text: 'Seu plano atual já é o maior disponível, mas os créditos de IA deste ciclo já foram consumidos.',
                icon: 'warning'
            });
        }

        async function generateConclusion(type = null) {
            if (!record.value?.uuid) {
                return;
            }

            try {
                generatingConclusion.value = true;
                const { data } = await ConclusionService.generate(record.value.uuid, type || conclusion.value?.type || 'standard');
                conclusion.value = (data && data.data) || null;
                conclusionDraft.value = normalizeConclusionHtml(conclusion.value?.text_content || '');
                await loadUsage();
            } catch (err) {
                const errorCode = err?.response?.data?.error;

                if (errorCode === 'usage_limit_reached') {
                    await showUsageLimitModal();
                    return;
                }

                if (errorCode === 'openrouter_not_configured') {
                    await Swal({
                        title: 'IA indisponível',
                        text: 'A integração com OpenRouter ainda não está configurada neste ambiente.',
                        icon: 'warning'
                    });
                    return;
                }

                if (errorCode === 'openrouter_failed') {
                    await Swal({
                        title: 'Falha ao gerar conclusão',
                        text: 'A IA não conseguiu responder agora. Tente novamente em instantes.',
                        icon: 'error'
                    });
                    return;
                }

                await Swal({
                    title: 'Ocorreu um problema ao gerar a conclusão',
                    text: 'Atualize a página e tente novamente. Se o erro persistir, entre em contato com o suporte.',
                    icon: 'error'
                });
            } finally {
                generatingConclusion.value = false;
            }
        }

        async function saveConclusion() {
            if (!conclusion.value?.uuid) {
                return;
            }

            try {
                savingConclusion.value = true;
                const { data } = await ConclusionService.updateText(conclusion.value.uuid, conclusionDraft.value);
                conclusion.value = {
                    ...conclusion.value,
                    ...((data && data.data) || {}),
                    pdf_url: conclusion.value.pdf_url
                };
                conclusionDraft.value = normalizeConclusionHtml(conclusion.value?.text_content || conclusionDraft.value);

                await Swal({
                    title: 'Conclusão atualizada',
                    text: 'As alterações do fisioterapeuta foram salvas com sucesso.',
                    icon: 'success'
                });
            } catch (err) {
                await Swal({
                    title: 'Ocorreu um problema ao salvar a conclusão',
                    text: 'Atualize a página e tente novamente. Se o erro persistir, entre em contato com o suporte.',
                    icon: 'error'
                });
            } finally {
                savingConclusion.value = false;
            }
        }

        async function exportPdf() {
            if (!conclusion.value?.uuid) {
                return;
            }

            try {
                exportingPdf.value = true;

                const imageBase64 = await captureMatrixPng(MATRIX_ELEMENT_ID);
                const { data } = await ConclusionService.exportPdf(conclusion.value.uuid, imageBase64);
                const pdfKey = data?.data?.pdf_key;

                if (pdfKey) {
                    conclusion.value = { ...conclusion.value, pdf_url: data?.data?.pdf_url };
                }

                const downloadUrl = ConclusionService.downloadPdfUrl(conclusion.value.uuid);
                const auth = AuthService.getAuthInfo();
                const blobRes = await fetch(downloadUrl, {
                    headers: { 'Authorization': auth && auth.token ? auth.token : '' }
                });
                const blob = await blobRes.blob();
                const blobUrl = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = `conclusao-${Date.now()}.pdf`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
            } catch (err) {
                await Swal({
                    title: 'Ocorreu um problema ao exportar o PDF',
                    text: 'Tente novamente em instantes. Se o erro persistir, entre em contato com o suporte.',
                    icon: 'error'
                });
            } finally {
                exportingPdf.value = false;
            }
        }

        function goToRecords() {
            if (!patient.value?.uuid) {
                return;
            }

            router.push({ name: 'records', params: { uuid: patient.value.uuid } });
        }

        function goToAvaliation() {
            if (!patient.value?.uuid || !record.value?.uuid) {
                return;
            }

            router.push({
                name: 'avaliation',
                params: {
                    patient_uuid: patient.value.uuid,
                    record_uuid: record.value.uuid
                }
            });
        }

        function formatDate(dateStr) {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleDateString('pt-BR');
        }

        function formatDateTime(dateStr) {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        function formatRelative(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '';

            const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
            if (diffSec < 60) return 'agora';
            const diffMin = Math.floor(diffSec / 60);
            if (diffMin < 60) return `há ${diffMin} min`;
            const diffHour = Math.floor(diffMin / 60);
            if (diffHour < 24) return `há ${diffHour}h`;
            const diffDay = Math.floor(diffHour / 24);
            if (diffDay < 30) return `há ${diffDay} ${diffDay === 1 ? 'dia' : 'dias'}`;
            const diffMonth = Math.floor(diffDay / 30);
            if (diffMonth < 12) return `há ${diffMonth} ${diffMonth === 1 ? 'mês' : 'meses'}`;
            const diffYear = Math.floor(diffMonth / 12);
            return `há ${diffYear} ${diffYear === 1 ? 'ano' : 'anos'}`;
        }

        function isStructuredCorrelation(correlation) {
            return Boolean(
                correlation &&
                typeof correlation === 'object' &&
                (correlation.finding_a || correlation.finding_b || correlation.clinical_significance)
            );
        }

        function formatCorrelation(correlation) {
            if (!correlation) return 'Correlação indisponível';
            if (typeof correlation === 'string') return correlation;
            if (typeof correlation === 'object') {
                return correlation.title || correlation.label || correlation.description || correlation.finding_a || JSON.stringify(correlation);
            }
            return String(correlation);
        }

        function escapeHtml(text = '') {
            return String(text)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        }

        function normalizeConclusionHtml(content = '') {
            // Desfaz o envelope da IA antes de qualquer coisa: o teste de HTML abaixo
            // dava positivo para o JSON cru (tem <h2> dentro da string) e devolvia o
            // ```json inteiro para a tela.
            const raw = unwrapAiHtml(content).trim();
            if (!raw) return '';

            if (/<(h1|h2|h3|p|ul|ol|li|div|br|strong|em)\b/i.test(raw)) {
                return raw;
            }

            const cleaned = raw
                .replace(/\r\n/g, '\n')
                .replace(/\r/g, '\n')
                .replace(/^s(?=[A-ZÁÀÂÃÉÈÊÍÌÎÓÒÔÕÚÙÛÇ])/g, '')
                .trim();

            const sections = cleaned.split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean);
            const html = sections.map((section) => {
                const lines = section.split('\n').map((line) => line.trim()).filter(Boolean);
                return lines.map((line) => {
                    if (/^(síntese clínica|análise funcional|análise da mobilidade|perfil de risco|conclusão|plano de intervenção|interpretação dos exames auxiliares)/i.test(line.replace(/[:-]\s*$/, ''))) {
                        return `<h2>${escapeHtml(line.replace(/[:-]\s*$/, ''))}</h2>`;
                    }

                    if (line.length <= 90 && !/[.!?]$/.test(line) && /[A-Za-zÀ-ÿ]/.test(line)) {
                        return `<h3>${escapeHtml(line)}</h3>`;
                    }

                    return `<p>${escapeHtml(line)}</p>`;
                }).join('');
            }).join('');

            return html || `<p>${escapeHtml(cleaned)}</p>`;
        }

        function onEditableInput(event) {
            conclusionDraft.value = event.target.innerHTML;
        }

        function syncEditableHtml() {
            const el = editableRef.value;
            const html = conclusionDraft.value;
            if (el && el.innerHTML !== html) {
                el.innerHTML = html || '';
            }
        }

        watch(conclusionDraft, async () => {
            await nextTick();
            syncEditableHtml();
        });

        watch(loading, async (isLoading) => {
            if (!isLoading) {
                await nextTick();
                syncEditableHtml();
            }
        });

        async function confirmRegenerate() {
            const confirmed = await Swal({
                title: 'Gerar nova conclusão?',
                text: 'A conclusão atual será perdida e substituída por uma nova gerada pela IA. Esta ação não pode ser desfeita.',
                icon: 'warning',
                buttons: ['Cancelar', 'Sim, gerar novamente'],
                dangerMode: true,
            });

            if (confirmed) {
                await generateConclusion();
            }
        }

        return {
            loading,
            savingConclusion,
            generatingConclusion,
            exportingPdf,
            patient,
            record,
            conclusion,
            conclusionDraft,
            editableRef,
            matrix,
            showBodyRiskMap,
            riskProfileChart,
            probabilityChart,
            generateConclusion,
            confirmRegenerate,
            saveConclusion,
            exportPdf,
            onEditableInput,
            goToRecords,
            goToAvaliation,
            formatDate,
            formatDateTime,
            formatRelative,
            formatCorrelation,
            isStructuredCorrelation
        };
    }
};
</script>

<style scoped>
/* Renderizado fora da área visível: serve só de fonte para o screenshot da matriz
   (display:none não é capturável pelo html-to-image). */
.export-matrix-stage {
    position: fixed;
    top: 0;
    left: -10000px;
    pointer-events: none;
}

/* Mesmo alinhamento do PDF: texto justificado. */
.conclusion-editable :deep(p),
.conclusion-editable :deep(li) {
    text-align: justify;
    hyphens: auto;
}

.conclusion-ai-cta {
    position: relative;
    overflow: hidden;
    border-color: rgba(8, 145, 178, 0.55) !important;
    background: linear-gradient(135deg, #0891b2 0%, #06b6d4 45%, #0e7490 100%) !important;
    color: #ffffff !important;
    box-shadow: 0 4px 10px rgba(8, 145, 178, 0.16);
}

.conclusion-ai-cta::before {
    content: '';
    position: absolute;
    top: 0;
    left: 100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.45), transparent);
    animation: conclusion-ai-shine 2s linear infinite;
}

.conclusion-ai-cta:hover {
    border-color: rgba(14, 116, 144, 1) !important;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.14), 0 6px 14px rgba(8, 145, 178, 0.22);
}

.conclusion-ai-cta:disabled::before,
.conclusion-ai-cta:disabled {
    animation: none;
}

.conclusion-ai-cta:disabled::before {
    display: none;
}

@keyframes conclusion-ai-shine {
    0% { left: 100%; }
    100% { left: -60%; }
}
</style>
